import { useState } from "react";
import Modal from "components/modal";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import { getUsageData } from "api/usage";
import useModal from "utils/hooks/use-modal";
import DetailCard from "components/detail-card";
import { useSearchParams } from "react-router-dom";
import { useStripe } from "@stripe/react-stripe-js";
import { Zap } from "lucide-react";
import ShimmerLoader from "components/shimmer-loader";
import PlanDetails from "sections/modal/plan-details";
import { getSubscriptionData } from "api/subscriptions";
import getFormattedDate from "utils/methods/getFormattedDate";
import capitalizeString from "utils/methods/capitalizeString";
import { getAllStripeProducts, createCheckoutSession } from "api/stripe";

const ChoosePlan = () => {
  const [searchParams] = useSearchParams();
  const planValue = searchParams.get("plan");

  const { openModal, closeModal, isOpen } = useModal(planValue ? true : false);

  // stripe hooks and states
  const stripe = useStripe();
  const [loading, setLoading] = useState(null);

  const { data: userUsageData } = useQuery("userUsage", getUsageData, {
    enabled: false,
  });

  const { data: stripeProducts, isLoading: stripeProductsLoading } = useQuery(
    "stripe-products",
    getAllStripeProducts,
    {
      retry: false,
      keepPreviousData: true,
    }
  );

  const {
    data: subscriptionData,
    error: subscriptionDataError,
    isLoading: subscriptionLoading,
  } = useQuery("userSubscription", getSubscriptionData, {
    retry: false,
    keepPreviousData: true,
  });

  const handleSubmitWithCheckout = async (event, priceId) => {
    event.preventDefault();
    setLoading(priceId);

    try {
      // Call your backend to create the checkout session
      const response = await createCheckoutSession({ priceId });

      // Redirect the user to Stripe Checkout
      const result = await stripe.redirectToCheckout({
        sessionId: response.id,
      });

      if (result.error) {
        toast.error(
          result.error.message || "Failed creating checkout session",
          {
            position: "top-right",
          }
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Error processing payment!", {
        position: "top-right",
      });
    } finally {
      setLoading(null);
    }
  };

  const isToBeRenewed = (date) => {
    const currentDate = new Date(Date.now());
    return new Date(date) > currentDate;
  };

  const ifAllDataLoaded = () => {
    return (
      !subscriptionLoading &&
      !stripeProductsLoading &&
      stripeProducts &&
      subscriptionData &&
      !subscriptionDataError
    );
  };

  const isSubscriptionActive = () => {
    return (
      !subscriptionLoading &&
      subscriptionData &&
      (subscriptionData.status === "active" ||
        subscriptionData.status === "trialing")
    );
  };

  const isPeriodMonth = () => {
    const allPrices = [];
    Object.keys(stripeProducts).forEach((el) => {
      allPrices.push(...stripeProducts[el]);
    });
    const priceInfo = allPrices.find(
      (el) => el.id === subscriptionData.planDuration.stripePriceId
    );
    return priceInfo.recurring.interval === "month";
  };

  const displayPrice = () => {
    const allPrices = [];
    Object.keys(stripeProducts).forEach((el) => {
      allPrices.push(...stripeProducts[el]);
    });
    const priceInfo = allPrices.find(
      (el) => el.id === subscriptionData.planDuration.stripePriceId
    );
    return (
      priceInfo.currency.toUpperCase() +
      " " +
      priceInfo.unit_amount / 100 +
      "/" +
      priceInfo.recurring.interval
    );
  };

  const getRenewsDisplayText = () => {
    if (subscriptionData.canceledAt) {
      return "Plan ends ";
    }

    if (
      subscriptionData.status === "trialing" &&
      isToBeRenewed(subscriptionData.endDate)
    ) {
      return "Trial Ends ";
    }

    if (
      subscriptionData.status !== "canceled" &&
      isToBeRenewed(subscriptionData.endDate)
    ) {
      return "Renews ";
    }

    return "Expired ";
  };

  return (
    <>
      <div className="w-full xl:max-w-[850px]">
        <div className="mb-[5px]">
          <button
            onClick={() => openModal()}
            className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold text-sm px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 active:scale-95 border border-blue-700/20 overflow-hidden"
          >
            {/* Animated background effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            {/* Content */}
            <Zap className="h-5 w-5 relative z-10 group-hover:animate-pulse" />
            <span className="relative z-10">Upgrade to Pro</span>
            
            {/* Shine effect */}
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
          </button>
        </div>
      </div>

      {isOpen && (
        <Modal isOpen={isOpen} closeModal={closeModal}>
          <PlanDetails
            loading={loading}
            products={stripeProducts}
            userUsageData={userUsageData}
            subscriptionData={subscriptionData}
            handleClickGetStarted={handleSubmitWithCheckout}
          />
        </Modal>
      )}
    </>
  );
};

export default ChoosePlan;
